import { useState, useEffect } from "react";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { KeyRound } from "lucide-react";
import { applyAccent, applyFontPair } from "@/lib/theme";

const DEFAULT_LOGIN_IMAGE = "https://images.unsplash.com/photo-1533554030380-20991a0f9c9e?crop=entropy&cs=srgb&fm=jpg&q=85";

export default function Login() {
  const { login } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [brand, setBrand] = useState({ structure_name: "", logo_url: "", login_image_url: "" });
  useEffect(() => { api.get("/public/branding").then((r) => { setBrand(r.data); applyAccent(r.data.accent_color); applyFontPair(r.data.font_pair); }).catch(() => {}); }, []);
  const Logo = ({ className, variant }) => {
    const dark = variant === "dark" || (variant === "auto" && document.documentElement.classList.contains("dark"));
    const src = (dark && brand.logo_dark_url) || brand.logo_url;
    return src ? <img src={src} alt={brand.structure_name} data-testid="login-logo" className={`object-contain rounded-xl ${dark && brand.logo_dark_url ? "" : "bg-white/90 p-1"} shrink-0 ${className}`} /> : null;
  };

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(email.trim(), password);
      toast.success("Accesso effettuato");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      <div
        className="hidden lg:block relative"
        data-testid="login-hero"
        style={{
          backgroundImage:
            `linear-gradient(180deg, rgba(44,34,30,0.15), rgba(44,34,30,0.75)), url('${brand.login_image_url || DEFAULT_LOGIN_IMAGE}')`,
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      >
        <div className="absolute bottom-14 left-14 text-white max-w-md animate-fade-up">
          <p className="text-xs uppercase tracking-[0.25em] mb-4 text-white/80" data-testid="login-tagline">{brand.login_tagline || "Gestionale Affittacamere"}</p>
          <div className="flex items-center gap-4 mb-4">
            <Logo className="w-16 h-16" variant="dark" />
            <h1 className="font-display text-6xl leading-none">FAAM WebServices</h1>
          </div>
          {brand.structure_name && <p className="text-xs uppercase tracking-[0.25em] mb-3 text-white/90" data-testid="login-structure-name">{brand.structure_name}</p>}
          <p className="text-white/85 leading-relaxed" data-testid="login-welcome">
            {brand.login_welcome || "Camere, prenotazioni, incassi e ricevute della tua struttura, in un unico posto elegante."}
          </p>
        </div>
      </div>

      <div className="flex items-center justify-center p-8 bg-background">
        <div className="w-full max-w-sm animate-fade-up">
          <div className="lg:hidden flex items-center gap-3 mb-2">
            <Logo className="w-11 h-11 border border-border" variant="auto" />
            <div className="font-display text-4xl">FAAM WebServices</div>
          </div>
          <h2 className="font-heading text-2xl font-semibold tracking-tight">Bentornato</h2>
          <p className="text-muted-foreground text-sm mb-8">Accedi per gestire la tua struttura</p>

          <form onSubmit={submit} className="space-y-4" data-testid="login-form">
            <div>
              <Label htmlFor="email" className="text-xs uppercase tracking-wider">Email</Label>
              <Input
                id="email"
                type="email"
                data-testid="login-email-input"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="nome@struttura.it"
                required
                className="mt-1.5"
              />
            </div>
            <div>
              <Label htmlFor="password" className="text-xs uppercase tracking-wider">Password</Label>
              <Input
                id="password"
                type="password"
                data-testid="login-password-input"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                required
                className="mt-1.5"
              />
            </div>
            <Button
              type="submit"
              data-testid="login-submit-button"
              disabled={loading}
              className="w-full bg-accent hover:bg-accent/90 text-accent-foreground h-11 rounded-full font-heading"
            >
              <KeyRound className="w-4 h-4 mr-2" />
              {loading ? "Accesso…" : "Accedi"}
            </Button>
          </form>
        </div>
      </div>
    </div>
  );
}
